import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, MapPin, Fuel, Package, CheckCircle2, Gauge, ShieldCheck } from "lucide-react";

export default function DriverTripDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: order } = useQuery({ queryKey: ["trip", id], queryFn: () => api.get(`/orders/${id}`).then((r) => r.data), refetchInterval: 10000 });
  // Last dispenser reading for the bowser attached to this order — used to
  // display a "must be ≥ N litres" hint on the opening + start-reading forms
  // and catch a backwards entry before the driver even submits.
  const { data: meterHint } = useQuery({
    queryKey: ["trip-meter-hint", id],
    queryFn: () => api.get(`/orders/${id}/dispenser-last-reading`).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 30000,
  });
  const lastReading = meterHint?.last_reading;
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);

  const [opening, setOpening] = useState({ dispenser_opening: 0, photo_url: "" });
  const [deliver, setDeliver] = useState({ otp: "", delivered_quantity: 0, dispenser_opening: 0, dispenser_closing: 0, temperature: 28.7, density: 0.833, photo_url: "", signature_url: "", density_test_pass: true, five_litre_test_pass: true, water_check_pass: true, location_confirmed: true });

  React.useEffect(() => {
    if (order?.quantity_litres) {
      setDeliver((d) => (d.delivered_quantity ? d : { ...d, delivered_quantity: order.quantity_litres }));
    }
  }, [order?.quantity_litres]);

  const reachMut = useMutation({
    mutationFn: () => api.post(`/orders/${id}/reach`, {}),
    onSuccess: () => { toast.success("Marked reached at customer site"); qc.invalidateQueries({ queryKey: ["trip", id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const openingMut = useMutation({
    mutationFn: (payload) => api.post(`/orders/${id}/dispenser-opening`, payload),
    onSuccess: (r) => { toast.success("Dispenser opening reading saved"); setOpeningOpen(false); setDeliver((d) => ({ ...d, dispenser_opening: r.data.dispenser_opening })); qc.invalidateQueries({ queryKey: ["trip", id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const deliverMut = useMutation({
    mutationFn: (payload) => api.post(`/orders/${id}/deliver`, payload),
    onSuccess: () => { toast.success("Delivered! Invoice generated."); setDeliverOpen(false); qc.invalidateQueries({ queryKey: ["trip", id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const sendOtpMut = useMutation({
    mutationFn: () => api.post(`/orders/${id}/send-delivery-otp`, {}),
    onSuccess: (r) => {
      const ch = r.data.channels || [];
      const masked = r.data.customer_phone_masked;
      if (ch.length > 0) toast.success(`OTP dispatched via ${ch.join(", ")}${masked ? " → " + masked : ""}`);
      else toast.warning("OTP dispatched (in-app only — SMS/WhatsApp providers not configured)");
      qc.invalidateQueries({ queryKey: ["trip", id] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to send OTP"),
  });

  // Dual-OTP start flow: driver arrives → runs tests → collects customer's
  // START OTP → dispensing begins → collects END OTP on completion.
  const [startOpen, setStartOpen] = useState(false);
  const [startForm, setStartForm] = useState({
    otp: "", start_reading: 0, density: 0.833,
    density_test_pass: true, water_check_pass: true, five_litre_test_pass: true,
  });
  const sendStartOtpMut = useMutation({
    mutationFn: () => api.post(`/orders/${id}/start-otp/send`, {}),
    onSuccess: (r) => toast.success(`Start-OTP dispatched via ${(r.data.delivered_via || []).join(", ") || "in-app"}`),
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const verifyStartOtpMut = useMutation({
    mutationFn: (payload) => api.post(`/orders/${id}/start-otp/verify`, payload),
    onSuccess: () => {
      toast.success("Start-OTP verified · dispensing authorised");
      setStartOpen(false);
      qc.invalidateQueries({ queryKey: ["trip", id] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  // "Open in Google Maps" — universal deep link that opens the native app
  // on Android/iOS and Google Maps web elsewhere.
  const openInMaps = () => {
    // The Order model uses site_lat/site_lng/site_address for the delivery
    // destination — the older delivery_* fields are kept for backward compat
    // on some records, so fall back to those if the primary ones aren't set.
    const lat = order?.site_lat ?? order?.delivery_lat;
    const lng = order?.site_lng ?? order?.delivery_lng;
    const addr = order?.site_address || order?.delivery_address || "";
    const dest = (lat && lng) ? `${lat},${lng}` : (addr ? encodeURIComponent(addr) : "");
    if (!dest) return toast.error("No destination coordinates or address on this order");
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, "_blank");
  };

  if (!order) return <div>Loading...</div>;

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="driver-trip-detail">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft size={14} className="mr-1" /> Back</Button>

      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Trip</div>
        <h1 className="font-display text-3xl font-black tracking-tight">{order.code}</h1>
        <div className="mt-2"><StatusBadge status={order.status} /></div>
      </div>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex items-start gap-3">
            <MapPin size={18} className="mt-0.5 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{order.site_name}</div>
              <div className="text-sm text-muted-foreground">{order.site_address}</div>
              <div className="mt-1 text-xs text-muted-foreground">{order.customer_name}</div>
              {order.site_lat && order.site_lng ? (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${order.site_lat},${order.site_lng}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
                  data-testid="btn-navigate-to-site"
                >
                  <MapPin size={12} /> Open in Google Maps &amp; Navigate
                </a>
              ) : (
                <div className="mt-1 text-[11px] text-orange-600">No coordinates set for this delivery.</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 border-t border-border/40 pt-3">
            <Fuel size={18} className="text-primary" />
            <div className="font-semibold">{order.quantity_litres} L</div>
            <div className="text-sm text-muted-foreground">@ ₹{order.price_per_litre}/L</div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3">
        {order.status === "dispatched" && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="driver-await-dispatch">
            Waiting for dispatcher to start this trip after water & quality checks. Once the trip
            is live you&apos;ll see the &quot;Mark reached at customer site&quot; button here.
          </div>
        )}
        {["in_transit"].includes(order.status) && !order.reached_at && (
          <Button size="lg" variant="outline" className="pill-btn w-full" onClick={() => reachMut.mutate()} data-testid="btn-reached-site">
            <MapPin size={16} className="mr-2" /> Mark reached at customer site
          </Button>
        )}
        {["dispatched", "loading", "in_transit"].includes(order.status) && !order.delivered_at && (
          <Button size="lg" variant="outline" className="pill-btn w-full" onClick={openInMaps} data-testid="btn-open-in-maps">
            <MapPin size={16} className="mr-2" /> Open in Google Maps
          </Button>
        )}
        {["in_transit"].includes(order.status) && order.reached_at && !order.start_otp_verified_at && (
          <>
            <Button
              size="lg"
              variant="secondary"
              className="pill-btn w-full"
              onClick={() => sendStartOtpMut.mutate()}
              disabled={sendStartOtpMut.isPending}
              data-testid="btn-send-start-otp"
            >
              <ShieldCheck size={16} className="mr-2" />
              {sendStartOtpMut.isPending ? "Sending…" : "Send START OTP to customer"}
            </Button>
            <Button
              size="lg"
              className="pill-btn w-full"
              onClick={() => setStartOpen(true)}
              disabled={!order.reached_at}
              data-testid="btn-verify-start-otp"
            >
              <CheckCircle2 size={16} className="mr-2" /> Verify START OTP + record quality tests
            </Button>
          </>
        )}
        {["in_transit"].includes(order.status) && order.reached_at && !order.dispenser_opening && (
          <Button size="lg" variant="outline" className="pill-btn w-full" onClick={() => setOpeningOpen(true)} data-testid="btn-dispenser-opening">
            <Gauge size={16} className="mr-2" /> Record dispenser OPENING (before delivery)
          </Button>
        )}
        {["dispatched", "loading", "in_transit"].includes(order.status) && (
          <Button
            size="lg"
            variant="secondary"
            className="pill-btn w-full"
            onClick={() => sendOtpMut.mutate()}
            disabled={sendOtpMut.isPending}
            data-testid="btn-send-otp-customer"
          >
            <ShieldCheck size={16} className="mr-2" />
            {order.otp_verified_at
              ? "OTP verified · Resend to customer"
              : sendOtpMut.isPending
                ? "Sending…"
                : "Send OTP to customer via SMS/WhatsApp"}
          </Button>
        )}
        {["in_transit", "loading"].includes(order.status) && (
          <Button size="lg" className="pill-btn w-full" onClick={() => setDeliverOpen(true)} data-testid="btn-complete-delivery" disabled={!order.reached_at || !order.dispenser_opening || !order.start_otp_verified_at}>
            <CheckCircle2 size={16} className="mr-2" /> Complete delivery (customer END OTP)
          </Button>
        )}
        {order.status !== "delivered" && (
          <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-semibold">Trip progress</div>
            <ul className="space-y-1">
              <li>{order.reached_at ? "✓" : "☐"} Reached customer site</li>
              <li>{order.dispenser_opening ? "✓" : "☐"} Dispenser opening reading</li>
              <li>{order.start_otp_verified_at ? "✓" : "☐"} Quality tests + customer START OTP</li>
              <li>{order.otp_verified_at ? "✓" : "☐"} Customer END OTP · delivery</li>
            </ul>
          </div>
        )}
      </div>

      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="max-w-lg" data-testid="driver-start-otp-dialog">
          <DialogHeader>
            <DialogTitle>Verify START OTP · quality tests</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-primary/5 border border-primary/30 p-2 text-xs">
              Show density, water test and 5-litre test results to the customer FIRST. They&apos;ll share the START OTP only after they&apos;re satisfied.
            </div>
            <div>
              <Label>Density (kg/L)</Label>
              <Input type="number" step="0.001" value={startForm.density}
                     onChange={(e) => setStartForm({ ...startForm, density: Number(e.target.value) })}
                     data-testid="drv-start-density" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border p-2">
                <Label className="text-xs">Density OK?</Label>
                <Switch checked={startForm.density_test_pass}
                        onCheckedChange={(v) => setStartForm({ ...startForm, density_test_pass: v })} />
              </div>
              <div className="rounded-md border p-2">
                <Label className="text-xs">Water test OK?</Label>
                <Switch checked={startForm.water_check_pass}
                        onCheckedChange={(v) => setStartForm({ ...startForm, water_check_pass: v })} />
              </div>
              <div className="rounded-md border p-2">
                <Label className="text-xs">5L test OK?</Label>
                <Switch checked={startForm.five_litre_test_pass}
                        onCheckedChange={(v) => setStartForm({ ...startForm, five_litre_test_pass: v })} />
              </div>
            </div>
            <div>
              <Label>Dispenser START reading (litres) <span className="text-status-cancelled">*</span></Label>
              <Input type="number" step="0.01" value={startForm.start_reading || ""}
                     onChange={(e) => setStartForm({ ...startForm, start_reading: Number(e.target.value) })}
                     data-testid="drv-start-reading" placeholder="e.g. 15200.00" />
              {typeof lastReading === "number" && lastReading > 0 && (
                <div className="mt-1 text-xs" data-testid="drv-start-reading-hint">
                  {startForm.start_reading && startForm.start_reading < lastReading ? (
                    <span className="text-status-cancelled font-medium">
                      ⚠ Must be ≥ {lastReading.toLocaleString()} L (last recorded reading for this bowser).
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Last recorded reading for this bowser: {lastReading.toLocaleString()} L. New reading must be ≥ that.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>Customer START OTP <span className="text-status-cancelled">*</span></Label>
              <Input value={startForm.otp}
                     onChange={(e) => setStartForm({ ...startForm, otp: e.target.value })}
                     data-testid="drv-start-otp" placeholder="4-digit code from customer"
                     maxLength={4} inputMode="numeric" />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => verifyStartOtpMut.mutate(startForm)}
              disabled={
                !startForm.otp ||
                !startForm.start_reading ||
                verifyStartOtpMut.isPending ||
                (typeof lastReading === "number" && lastReading > 0 && startForm.start_reading < lastReading)
              }
              data-testid="btn-drv-confirm-start-otp"
            >
              {verifyStartOtpMut.isPending ? "Verifying…" : "Verify & authorise dispensing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openingOpen} onOpenChange={setOpeningOpen}>
        <DialogContent data-testid="driver-opening-dialog">
          <DialogHeader><DialogTitle>Dispenser opening reading</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Opening meter (litres) <span className="text-status-cancelled">*</span></Label>
              <Input type="number" step="0.01" value={opening.dispenser_opening || ""} onChange={(e) => setOpening({ ...opening, dispenser_opening: Number(e.target.value) })} data-testid="drv-open-reading" placeholder="e.g. 45230.50" />
              <div className="mt-1 text-xs text-muted-foreground">Record the dispenser meter BEFORE you start pumping fuel.</div>
              {typeof lastReading === "number" && lastReading > 0 && (
                <div className="mt-1 text-xs" data-testid="drv-open-reading-hint">
                  {opening.dispenser_opening && opening.dispenser_opening < lastReading ? (
                    <span className="text-status-cancelled font-medium">
                      ⚠ Must be ≥ {lastReading.toLocaleString()} L (last recorded for this bowser).
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Last recorded reading for this bowser: {lastReading.toLocaleString()} L.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-md border border-primary/40 bg-primary/5 p-2 text-xs">
              This is the reference point for calculating exact quantity delivered to this customer.
            </div>
          </div>
          <DialogFooter><Button onClick={() => openingMut.mutate(opening)} disabled={
            !opening.dispenser_opening ||
            (typeof lastReading === "number" && lastReading > 0 && opening.dispenser_opening < lastReading)
          } data-testid="btn-drv-confirm-opening">Save opening reading</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <DialogContent className="max-w-lg" data-testid="driver-delivery-dialog">
          <DialogHeader><DialogTitle>Complete delivery</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-muted/40 p-2 text-xs">
              Dispenser opening: <b className="font-mono">{order.dispenser_opening?.toFixed(2) || "—"}</b> L
            </div>
            <div>
              <Label>Dispenser closing reading (L) <span className="text-status-cancelled">*</span></Label>
              <Input type="number" step="0.01" value={deliver.dispenser_closing || ""} onChange={(e) => {
                const closing = Number(e.target.value);
                const qty = Math.max(0, closing - (order.dispenser_opening || 0));
                setDeliver({ ...deliver, dispenser_closing: closing, delivered_quantity: Number(qty.toFixed(2)) });
              }} data-testid="drv-close-reading" />
              {deliver.dispenser_closing > 0 && (
                <div className="mt-1 text-xs text-primary font-semibold">Sale to this customer: {deliver.delivered_quantity.toFixed(2)} L</div>
              )}
            </div>

            <div className="rounded-md border border-border/60 p-3 space-y-2">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">Customer tests</div>
              <div className="flex items-center justify-between text-sm"><Label>Density test passed</Label><Switch checked={deliver.density_test_pass} onCheckedChange={(v) => setDeliver({ ...deliver, density_test_pass: v })} data-testid="drv-test-density" /></div>
              <div className="flex items-center justify-between text-sm"><Label>5-litre quantity test passed</Label><Switch checked={deliver.five_litre_test_pass} onCheckedChange={(v) => setDeliver({ ...deliver, five_litre_test_pass: v })} data-testid="drv-test-5l" /></div>
              <div className="flex items-center justify-between text-sm"><Label>Water check passed</Label><Switch checked={deliver.water_check_pass} onCheckedChange={(v) => setDeliver({ ...deliver, water_check_pass: v })} data-testid="drv-test-water" /></div>
            </div>

            <div>
              <Label>Customer OTP {order.otp_verified_at && <span className="text-xs text-emerald-600">(pre-verified by customer)</span>}</Label>
              <Input value={deliver.otp} onChange={(e) => setDeliver({ ...deliver, otp: e.target.value })} placeholder="4-digit OTP" maxLength={4} inputMode="numeric" data-testid="drv-deliver-otp" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Delivered qty (auto)</Label><Input type="number" value={deliver.delivered_quantity} onChange={(e) => setDeliver({ ...deliver, delivered_quantity: Number(e.target.value) })} data-testid="drv-deliver-qty" /></div>
              <div><Label>Temp (°C)</Label><Input type="number" step="0.1" value={deliver.temperature} onChange={(e) => setDeliver({ ...deliver, temperature: Number(e.target.value) })} /></div>
              <div><Label>Density</Label><Input type="number" step="0.001" value={deliver.density} onChange={(e) => setDeliver({ ...deliver, density: Number(e.target.value) })} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={() => deliverMut.mutate({ ...deliver, dispenser_opening: order.dispenser_opening || 0 })} disabled={!deliver.otp || !deliver.dispenser_closing} data-testid="btn-drv-confirm-deliver">Complete delivery</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {order.status === "delivered" && (
        <Card className="border-status-delivered/40 bg-status-delivered/5">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="mx-auto mb-2 text-status-delivered" size={32} />
            <div className="font-display text-lg font-bold">Trip completed</div>
            <div className="text-sm text-muted-foreground">Delivered {order.delivered_quantity}L · Invoice generated.</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
